import { useMemo } from 'react';
import { useLiveRxQuery } from 'rxdb/plugins/react';
import { useStableQuery } from '../../db/useStableQuery';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { formatFindSlipNumber, type FindDocType } from '@ast-data/shared';
import { findingLinkPath } from '../../components/gridDeepLink';
import { linkClick } from '../../components/routerLink';
import { useFamilyProjectIds } from '../projects/useFamilyProjectIds';

import { compareNatural, compareText } from '../../utils/compare';

/**
 * The datings of every Fund attached to this Befund, so the person writing the
 * Befund's own Vorläufige Datierung can weigh what was actually found in it.
 *
 * READ-ONLY, and deliberately not a suggestion: the finds inform the judgement,
 * they do not make it. Every dating is listed as it stands, with a way through
 * to the Fundzettel it came from.
 */

/** A Fund reaches its Befund directly, or through one of its descriptions. */
function linksToFinding(
    find: FindDocType,
    findingId: string,
    planumIds: Set<string>,
    profileIds: Set<string>
): boolean {
    return (
        find.findingIds.includes(findingId) ||
        find.planumIds.some((id) => planumIds.has(id)) ||
        find.profileIds.some((id) => profileIds.has(id))
    );
}

export default function FindDatingList({
    projectId,
    findingId,
    findingNumber,
    planumIds,
    profileIds,
    onNavigate
}: {
    projectId: string;
    findingId: string;
    /** The stored Befundnummer — what the Fundliste link filters on. */
    findingNumber: string;
    /** This Befund's Planum description ids. */
    planumIds: string[];
    /** This Befund's Profil description ids. */
    profileIds: string[];
    /** Guard-aware navigation — the Befund form may have unsaved edits. */
    onNavigate: (to: string) => void;
}) {
    const intl = useIntl();
    // Linked projects share Befunde, so a Fund recorded under a sibling
    // Maßnahme can hang off this one — the same family scope the Befund's own
    // number check uses.
    const familyIds = useFamilyProjectIds(projectId);
    const findsQuery = useStableQuery<FindDocType>({
        selector: { projectId: { $in: familyIds } }
    });
    const { results: findsDocs } = useLiveRxQuery<FindDocType>({
        collection: 'finds',
        query: findsQuery
    });
    const finds = useMemo(
        () => findsDocs.map((doc) => doc.toMutableJSON() as FindDocType),
        [findsDocs]
    );
    // The field tool mounts no Fundzettel editor (see DescriptionReferences),
    // so there the numbers are text rather than dead links.
    const inField = useLocation().pathname.startsWith('/field');

    const { dated, undatedCount } = useMemo(() => {
        const planums = new Set(planumIds);
        const profiles = new Set(profileIds);
        const linked = (finds as FindDocType[]).filter((find) =>
            linksToFinding(find, findingId, planums, profiles)
        );
        const withDating = linked.filter((find) =>
            find.preliminaryDating.trim()
        );
        // By dating first, so identical ones read as a block and the weight of
        // each is visible at a glance; the Fundzettelnummer only orders within.
        withDating.sort(
            (firstFind, secondFind) =>
                compareText(
                    firstFind.preliminaryDating,
                    secondFind.preliminaryDating
                ) ||
                compareNatural(
                    formatFindSlipNumber(firstFind),
                    formatFindSlipNumber(secondFind)
                )
        );
        return {
            dated: withDating,
            undatedCount: linked.length - withDating.length
        };
    }, [finds, findingId, planumIds, profileIds]);

    /**
     * The Fundliste lists this project's Funde only, so the button counts
     * those — a sibling phase's Fund still shows in the datings above.
     */
    const listCount = useMemo(() => {
        const planums = new Set(planumIds);
        const profiles = new Set(profileIds);
        return (finds as FindDocType[]).filter(
            (find) =>
                find.projectId === projectId &&
                linksToFinding(find, findingId, planums, profiles)
        ).length;
    }, [finds, projectId, findingId, planumIds, profileIds]);
    /**
     * Every Fund of this Befund in the Fundliste, dated or not — the datings
     * here are a summary, the list is where the Funde are worked on. Office
     * only, like the per-Fund links: the field tool has no Fundliste.
     */
    // The number AS STORED: the list's filter compares against it untrimmed.
    const listTo = findingLinkPath(
        `/office/projects/${projectId}/finds`,
        findingNumber
    );
    const listLink = !inField && listCount > 0 && findingNumber.trim() && (
        <Button
            size="small"
            component={RouterLink}
            to={listTo}
            onClick={linkClick(() => onNavigate(listTo))}
            endIcon={<OpenInNewIcon fontSize="small" />}
            sx={{ mt: 1 }}
        >
            <FormattedMessage
                id="finding.findDatings.showInList"
                values={{ count: listCount }}
            />
        </Button>
    );

    if (dated.length === 0) {
        return (
            <Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    <FormattedMessage
                        id={
                            undatedCount > 0
                                ? 'finding.findDatings.noneDated'
                                : 'finding.findDatings.empty'
                        }
                        values={{ count: undatedCount }}
                    />
                </Typography>
                {listLink}
            </Box>
        );
    }

    return (
        <Box>
            <Typography
                variant="subtitle2"
                sx={{ mb: 1, color: 'text.secondary' }}
            >
                {/* The PROPORTION is what the reader needs — a dating drawn
                    from four of twelve finds is a different claim than one
                    drawn from four of four. */}
                <FormattedMessage
                    id={
                        undatedCount > 0
                            ? 'finding.findDatings.titlePartial'
                            : 'finding.findDatings.title'
                    }
                    values={{
                        count: dated.length,
                        total: dated.length + undatedCount
                    }}
                />
            </Typography>
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <FormattedMessage id="find.field.findSlipNumber" />
                            </TableCell>
                            <TableCell>
                                <FormattedMessage id="find.field.preliminaryDating" />
                            </TableCell>
                            <TableCell>
                                <FormattedMessage id="find.field.label" />
                            </TableCell>
                            {/* The action column carries the icon alone; its
                                heading would say nothing a reader needs. */}
                            <TableCell padding="checkbox" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {dated.map((find) => {
                            const to = `/office/projects/${find.projectId}/finds/${find.id}/edit`;
                            const number =
                                formatFindSlipNumber(find) ||
                                intl.formatMessage({
                                    id: 'finding.findDatings.noNumber'
                                });
                            return (
                                <TableRow key={find.id} hover>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {number}
                                    </TableCell>
                                    <TableCell>
                                        {find.preliminaryDating}
                                    </TableCell>
                                    <TableCell sx={{ color: 'text.secondary' }}>
                                        {find.label}
                                    </TableCell>
                                    <TableCell padding="checkbox">
                                        {!inField && (
                                            <Tooltip
                                                title={
                                                    <FormattedMessage id="finding.findDatings.open" />
                                                }
                                            >
                                                {/* A real anchor, so a
                                                    Fundzettel can be opened
                                                    BESIDE the Befund rather
                                                    than instead of it — see
                                                    routerLink. */}
                                                <IconButton
                                                    size="small"
                                                    component={RouterLink}
                                                    to={to}
                                                    onClick={linkClick(() =>
                                                        onNavigate(to)
                                                    )}
                                                    aria-label={intl.formatMessage(
                                                        {
                                                            id: 'finding.findDatings.open'
                                                        }
                                                    )}
                                                >
                                                    <OpenInNewIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {listLink}
        </Box>
    );
}
